import { useEffect, useState } from "react";
import { api } from "../api";
import "bootstrap/dist/css/bootstrap.min.css";

const ROLES = ["admin", "tech", "viewer"];

const ROLE_BADGE = {
  admin:  { label: "Admin",        bg: "#dc3545" },
  tech:   { label: "Técnico",      bg: "#0d6efd" },
  viewer: { label: "Visualizador", bg: "#6c757d" },
};

export default function Usuarios({ onLogout }) {
  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(true);

  // novo usuário
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole]         = useState("tech");
  const [saving, setSaving]           = useState(false);

  // reset de senha
  const [resetId, setResetId]         = useState(null);
  const [resetPassword, setResetPassword] = useState("");

  // mensagem de feedback
  const [msg, setMsg]                 = useState(null); // {text, type}

  const showMsg = (text, type = "success") => {
    setMsg({ text, type });
    setTimeout(() => setMsg(null), 3000);
  };

  const loadUsers = async () => {
    try {
      const res = await api.get("/usuarios/list");
      setUsers(res.data);
    } catch (err) {
      console.error(err);
      showMsg("Erro ao carregar usuários.", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); }, []);

  // ── Cadastrar ──────────────────────────────────────────
  const handleAdd = async () => {
    if (!newUsername || !newPassword) {
      return showMsg("Preencha usuário e senha.", "warning");
    }
    setSaving(true);
    try {
      await api.post("/auth/register", {
        username: newUsername,
        password: newPassword,
        role: newRole,
      });
      setNewUsername("");
      setNewPassword("");
      setNewRole("tech");
      showMsg("Usuário cadastrado!");
      loadUsers();
    } catch (err) {
      showMsg(err.response?.data?.error || "Erro ao cadastrar.", "danger");
    } finally {
      setSaving(false);
    }
  };

  // ── Alterar role ───────────────────────────────────────
  const handleRoleChange = async (id, role) => {
    try {
      await api.put(`/usuarios/update/${id}`, { role });
      setUsers(users.map(u => u.id === id ? { ...u, role } : u));
      showMsg("Role atualizado!");
    } catch (err) {
      showMsg(err.response?.data?.error || "Erro ao atualizar role.", "danger");
    }
  };

  // ── Resetar senha ──────────────────────────────────────
  const handleResetPassword = async (id) => {
    if (!resetPassword || resetPassword.length < 6) {
      return showMsg("Senha deve ter no mínimo 6 caracteres.", "warning");
    }
    try {
      await api.put(`/usuarios/reset-password/${id}`, { password: resetPassword });
      setResetId(null);
      setResetPassword("");
      showMsg("Senha resetada!");
    } catch (err) {
      showMsg(err.response?.data?.error || "Erro ao resetar senha.", "danger");
    }
  };

  // ── Deletar ────────────────────────────────────────────
  const handleDelete = async (id, username) => {
    if (!window.confirm(`Excluir o usuário "${username}"?`)) return;
    try {
      await api.delete(`/usuarios/delete/${id}`);
      setUsers(users.filter(u => u.id !== id));
      showMsg("Usuário excluído.");
    } catch (err) {
      showMsg(err.response?.data?.error || "Erro ao excluir.", "danger");
    }
  };

  // ── JSX ────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5" }}>

      {/* Navbar */}
      <nav className="navbar navbar-dark bg-dark mb-4 d-flex justify-content-center gap-3">
        <span className="navbar-brand m-0 fs-5">IClickMap — Usuários</span>
        <button className="btn btn-danger" onClick={onLogout}>Sair</button>
      </nav>

      <div className="container" style={{ maxWidth: 800 }}>

        {/* Feedback */}
        {msg && (
          <div className={`alert alert-${msg.type} py-2`}>
            {msg.text}
          </div>
        )}

        {/* ── Cadastrar novo usuário ── */}
        <div className="card shadow-sm mb-4">
          <div className="card-header fw-bold">Novo Usuário</div>
          <div className="card-body">
            <div className="row g-2">
              <div className="col">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Usuário"
                  value={newUsername}
                  onChange={e => setNewUsername(e.target.value)}
                />
              </div>
              <div className="col">
                <input
                  type="password"
                  className="form-control"
                  placeholder="Senha (mín. 6 caracteres)"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                />
              </div>
              <div className="col-auto">
                <select
                  className="form-select"
                  value={newRole}
                  onChange={e => setNewRole(e.target.value)}
                >
                  <option value="admin">Admin</option>
                  <option value="tech">Técnico</option>
                  <option value="viewer">Visualizador</option>
                </select>
              </div>
              <div className="col-auto">
                <button
                  className="btn btn-success"
                  onClick={handleAdd}
                  disabled={saving}
                >
                  {saving ? "Salvando..." : "Cadastrar"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Tabela de usuários ── */}
        <div className="card shadow-sm">
          <div className="card-header fw-bold">
            Usuários cadastrados ({users.length})
          </div>
          <div className="card-body p-0">
            {loading ? (
              <p className="text-center p-4">Carregando...</p>
            ) : (
              <table className="table table-hover mb-0">
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Usuário</th>
                    <th>Role</th>
                    <th>Criado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <>
                      <tr key={u.id}>
                        <td className="text-muted">{u.id}</td>
                        <td className="fw-semibold">{u.username}</td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            style={{ width: 140 }}
                            value={u.role}
                            onChange={e => handleRoleChange(u.id, e.target.value)}
                          >
                            {ROLES.map(r => (
                              <option key={r} value={r}>
                                {ROLE_BADGE[r].label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="text-muted" style={{ fontSize: 13 }}>
                          {new Date(u.created_at).toLocaleDateString("pt-BR")}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => {
                                setResetId(resetId === u.id ? null : u.id);
                                setResetPassword("");
                              }}
                            >
                              🔑 Senha
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(u.id, u.username)}
                            >
                              Excluir
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Linha de reset de senha (expande inline) */}
                      {resetId === u.id && (
                        <tr key={`reset-${u.id}`} style={{ background: "#fff8e1" }}>
                          <td colSpan={5}>
                            <div className="d-flex gap-2 align-items-center px-2 py-1">
                              <span className="text-muted" style={{ fontSize: 13 }}>
                                Nova senha para <strong>{u.username}</strong>:
                              </span>
                              <input
                                type="password"
                                className="form-control form-control-sm"
                                style={{ maxWidth: 220 }}
                                placeholder="Mínimo 6 caracteres"
                                value={resetPassword}
                                onChange={e => setResetPassword(e.target.value)}
                              />
                              <button
                                className="btn btn-sm btn-warning"
                                onClick={() => handleResetPassword(u.id)}
                              >
                                Confirmar
                              </button>
                              <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={() => { setResetId(null); setResetPassword(""); }}
                              >
                                Cancelar
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}